import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type ToastKind = 'success' | 'error' | 'info';
type Toast = { id: number; kind: ToastKind; title: string; body?: string };

type ToastApi = {
  show: (t: Omit<Toast, 'id'>) => void;
  success: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
  info: (title: string, body?: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const api = useMemo<ToastApi>(() => {
    const show = (t: Omit<Toast, 'id'>) => {
      const id = ++counter.current;
      setToasts((prev) => [...prev, { ...t, id }]);
      setTimeout(() => remove(id), 4500);
    };
    return {
      show,
      success: (title, body) => show({ kind: 'success', title, body }),
      error: (title, body) => show({ kind: 'error', title, body }),
      info: (title, body) => show({ kind: 'info', title, body }),
    };
  }, [remove]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 max-w-sm">
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onClose={() => remove(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

function ToastCard({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const colors = {
    success: 'border-green-200 bg-green-50',
    error: 'border-red-200 bg-red-50',
    info: 'border-line bg-surface',
  }[toast.kind];
  const icon = {
    success: '✓',
    error: '✕',
    info: 'i',
  }[toast.kind];
  const iconColor = {
    success: 'bg-green-600 text-white',
    error: 'bg-red-600 text-white',
    info: 'bg-ink text-white',
  }[toast.kind];

  useEffect(() => {
    const t = setTimeout(onClose, 4500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`flex gap-3 rounded-2xl border ${colors} px-4 py-3 shadow-cardHover animate-[toast_180ms_ease-out]`}>
      <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${iconColor}`}>
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight">{toast.title}</p>
        {toast.body ? <p className="mt-0.5 text-xs text-ink-muted">{toast.body}</p> : null}
      </div>
      <button onClick={onClose} aria-label="Dismiss" className="text-ink-subtle hover:text-ink">
        ✕
      </button>
    </div>
  );
}
